import { c as _c } from "react/compiler-runtime";
import CircleLoader, { CircleLoaderSize } from "@/components/distribution-plan-tool/common/CircleLoader";
import WaveDropsEmptyPlaceholder from "@/components/waves/drops/WaveDropsEmptyPlaceholder";
import { useUnreadDivider } from "@/contexts/wave/UnreadDividerContext";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import type { useVirtualizedWaveDrops } from "@/hooks/useVirtualizedWaveDrops";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import type { RefObject, Ref } from "react";
import { WaveDropsMessageListSection } from "./WaveDropsMessageListSection";
import { WaveDropsTypingIndicator } from "./WaveDropsTypingIndicator";
type WaveMessagesResult = ReturnType<typeof useVirtualizedWaveDrops>["waveMessages"];
interface WaveDropsContentProps {
  readonly waveMessages: WaveMessagesResult;
  readonly dropId: string | null;
  readonly scrollContainerRef: RefObject<HTMLDivElement | null>;
  readonly scrollContainerCallbackRef?: Ref<HTMLDivElement> | undefined;
  readonly bottomAnchorRef: RefObject<HTMLDivElement | null>;
  readonly bottomAnchorCallbackRef?: Ref<HTMLDivElement> | undefined;
  readonly onTopIntersection: () => void;
  readonly onReply: ({
    drop,
    partId
  }: {
    drop: ApiDrop;
    partId: number;
  }) => void;
  readonly queueSerialTarget: (serialNo: number) => void;
  readonly activeDrop: ActiveDropState | null;
  readonly serialTarget: number | null;
  readonly targetDropRef: RefObject<HTMLDivElement | null>;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly isAtBottom: boolean;
  readonly scrollToBottom: () => void;
  readonly typingMessage: string | null;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
  readonly pendingCount: number;
  readonly onRevealPending: () => void;
  readonly bottomPaddingClassName?: string | undefined;
  readonly boostedDrops?: ApiDrop[] | undefined;
  readonly onBoostedDropClick?: ((serialNo: number) => void) | undefined;
  readonly onScrollToUnread?: ((serialNo: number) => void) | undefined;
  readonly unreadCount?: number | undefined;
  readonly autoCollapseSerials?: ReadonlySet<number> | undefined;
  readonly suspendLightDropHydration?: boolean | undefined;
}
export const WaveDropsContent: React.FC<WaveDropsContentProps> = t0 => {
  const $ = _c(38);
  const {
    waveMessages,
    dropId,
    scrollContainerRef,
    scrollContainerCallbackRef,
    bottomAnchorRef,
    bottomAnchorCallbackRef,
    onTopIntersection,
    onReply,
    queueSerialTarget,
    activeDrop,
    serialTarget,
    targetDropRef,
    onQuoteClick,
    isAtBottom,
    scrollToBottom,
    typingMessage,
    onDropContentClick,
    pendingCount,
    onRevealPending,
    bottomPaddingClassName,
    boostedDrops,
    onBoostedDropClick,
    onScrollToUnread,
    unreadCount,
    autoCollapseSerials,
    suspendLightDropHydration: t1
  } = t0;
  const suspendLightDropHydration = t1 === undefined ? false : t1;
  const {
    unreadDividerSerialNo,
    setUnreadDividerSerialNo
  } = useUnreadDivider();
  let t2;
  if ($[0] !== setUnreadDividerSerialNo) {
    t2 = () => {
      setUnreadDividerSerialNo(null);
    };
    $[0] = setUnreadDividerSerialNo;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleDismissUnread = t2;
  const dropsCount = waveMessages?.drops.length ?? 0;
  const isInitialLoading = !!waveMessages?.isLoading && !waveMessages.isLoadingNextPage && dropsCount === 0;
  const isHydrating = !waveMessages;
  if (isHydrating || isInitialLoading) {
    let t3;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-10"><CircleLoader size={CircleLoaderSize.XXLARGE} /></div>;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    return t3;
  }
  if (dropsCount === 0) {
    let t3;
    if ($[3] !== dropId) {
      t3 = <WaveDropsEmptyPlaceholder dropId={dropId} />;
      $[3] = dropId;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    return t3;
  }
  let t3;
  if ($[5] !== activeDrop || $[6] !== autoCollapseSerials || $[7] !== boostedDrops || $[8] !== bottomAnchorCallbackRef || $[9] !== bottomAnchorRef || $[10] !== bottomPaddingClassName || $[11] !== dropId || $[12] !== handleDismissUnread || $[13] !== isAtBottom || $[14] !== onBoostedDropClick || $[15] !== onDropContentClick || $[16] !== onQuoteClick || $[17] !== onReply || $[18] !== onRevealPending || $[19] !== onScrollToUnread || $[20] !== onTopIntersection || $[21] !== pendingCount || $[22] !== queueSerialTarget || $[23] !== scrollContainerCallbackRef || $[24] !== scrollContainerRef || $[25] !== scrollToBottom || $[26] !== serialTarget || $[27] !== suspendLightDropHydration || $[28] !== targetDropRef || $[29] !== unreadCount || $[30] !== unreadDividerSerialNo || $[31] !== waveMessages) {
    t3 = <WaveDropsMessageListSection waveMessages={waveMessages} dropId={dropId} scrollContainerRef={scrollContainerRef} scrollContainerCallbackRef={scrollContainerCallbackRef} bottomAnchorRef={bottomAnchorRef} bottomAnchorCallbackRef={bottomAnchorCallbackRef} onTopIntersection={onTopIntersection} onReply={onReply} queueSerialTarget={queueSerialTarget} activeDrop={activeDrop} serialTarget={serialTarget} targetDropRef={targetDropRef} onQuoteClick={onQuoteClick} isAtBottom={isAtBottom} scrollToBottom={scrollToBottom} onDropContentClick={onDropContentClick} pendingCount={pendingCount} onRevealPending={onRevealPending} bottomPaddingClassName={bottomPaddingClassName} unreadDividerSerialNo={unreadDividerSerialNo} unreadCount={unreadCount} boostedDrops={boostedDrops} onBoostedDropClick={onBoostedDropClick} onScrollToUnread={onScrollToUnread} onDismissUnread={handleDismissUnread} autoCollapseSerials={autoCollapseSerials} suspendLightDropHydration={suspendLightDropHydration} />;
    $[5] = activeDrop;
    $[6] = autoCollapseSerials;
    $[7] = boostedDrops;
    $[8] = bottomAnchorCallbackRef;
    $[9] = bottomAnchorRef;
    $[10] = bottomPaddingClassName;
    $[11] = dropId;
    $[12] = handleDismissUnread;
    $[13] = isAtBottom;
    $[14] = onBoostedDropClick;
    $[15] = onDropContentClick;
    $[16] = onQuoteClick;
    $[17] = onReply;
    $[18] = onRevealPending;
    $[19] = onScrollToUnread;
    $[20] = onTopIntersection;
    $[21] = pendingCount;
    $[22] = queueSerialTarget;
    $[23] = scrollContainerCallbackRef;
    $[24] = scrollContainerRef;
    $[25] = scrollToBottom;
    $[26] = serialTarget;
    $[27] = suspendLightDropHydration;
    $[28] = targetDropRef;
    $[29] = unreadCount;
    $[30] = unreadDividerSerialNo;
    $[31] = waveMessages;
    $[32] = t3;
  } else {
    t3 = $[32];
  }
  let t4;
  if ($[33] !== typingMessage) {
    t4 = <WaveDropsTypingIndicator typingMessage={typingMessage} />;
    $[33] = typingMessage;
    $[34] = t4;
  } else {
    t4 = $[34];
  }
  let t5;
  if ($[35] !== t3 || $[36] !== t4) {
    t5 = <>{t3}{t4}</>;
    $[35] = t3;
    $[36] = t4;
    $[37] = t5;
  } else {
    t5 = $[37];
  }
  return t5;
};